// Dishes.js — admin Dish Catalog. Review AI-generated descriptions + categories
// and override them; overrides are remembered (the enricher won't regenerate).
import React, { useCallback, useEffect, useState } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { ENDPOINTS } from '../../config/endpoints';

const PORTAL_API = ENDPOINTS.portalApi;
const TIERS = [
  { id: '', label: 'Auto (by category)' },
  { id: 'veg-curry', label: 'Veg curry · $14.99' },
  { id: 'nonveg-curry', label: 'Non-veg curry · $15.99' },
  { id: 'premium', label: 'Premium · $16.99' },
  { id: 'indo-chinese', label: 'Indo-Chinese · $10.99' },
  { id: 'biryani', label: 'Biryani · $14.99' },
  { id: 'bread', label: 'Bread · $2.99' },
  { id: 'dessert', label: 'Dessert · $3.99' },
  { id: 'drink', label: 'Drink · $1.99' },
  { id: 'appetizer', label: 'Appetizer · $5.99' },
];
const CATEGORIES = [
  'Live Station', 'Appetizers', 'Soups & Salads', 'Curries',
  'Rice & Biryani', 'Breads', 'Sides & Chaat', 'Desserts', 'Beverages',
];

async function authHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  const h = { 'Content-Type': 'application/json' };
  if (token) h.Authorization = `Bearer ${token}`;
  return h;
}

const Badge = ({ src }) =>
  src === 'staff' ? <span className="rounded bg-emerald-600/20 text-emerald-300 px-1.5 py-0.5 text-[10px]">edited</span>
  : src === 'ai' ? <span className="rounded bg-sky-600/20 text-sky-300 px-1.5 py-0.5 text-[10px]">AI</span>
  : null;

export default function Dishes() {
  const [dishes, setDishes] = useState([]);
  const [q, setQ] = useState('');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [status, setStatus] = useState('');
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setStatus('Loading…');
    try {
      const res = await fetch(`${PORTAL_API}/api/dishes?all=1`, { headers: await authHeaders() }).then((r) => r.json());
      setDishes(res.dishes || []);
      setStatus('');
    } catch {
      setStatus('Could not load catalog.');
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const update = (id, fields) => setDishes((ds) => ds.map((d) => (d.id === id ? { ...d, ...fields } : d)));

  async function save(d) {
    setSavingId(d.id);
    try {
      const res = await fetch(`${PORTAL_API}/api/dishes`, {
        method: 'PATCH',
        headers: await authHeaders(),
        body: JSON.stringify({ id: d.id, description: d.description, category: d.category, priceTier: d.priceTier || '', priceCents: d.priceCents || 0, panCostCents: d.panCostCents || 0 }),
      }).then((r) => r.json());
      if (res.ok && res.dish) update(d.id, res.dish);
    } catch {
      /* ignore */
    }
    setSavingId(null);
  }

  const shown = dishes.filter(
    (d) => (!pendingOnly || d.descStatus === 'pending') && (!q || d.name.toLowerCase().includes(q.toLowerCase())),
  );
  const pendingCount = dishes.filter((d) => d.descStatus === 'pending').length;

  return (
    <div className="space-y-5 max-w-4xl">
      <div className="ui-card">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-lg font-semibold text-brand">Dish Catalog <span className="text-neutral-500 font-normal">({dishes.length})</span></h2>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search dishes…" className="ui-input" style={{ width: 'auto' }} />
            <label className="flex items-center gap-1 text-sm text-neutral-400"><input type="checkbox" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} /> Needs review ({pendingCount})</label>
            <button onClick={load} className="ui-btn-outline">Refresh</button>
          </div>
        </div>
        <p className="mt-2 text-sm text-neutral-400">
          Descriptions &amp; categories are auto-generated by AI, then used for the website and menu suggestions. Edit
          anything here — your changes are remembered and won&rsquo;t be overwritten. {status && <span className="text-neutral-500">· {status}</span>}
        </p>
      </div>

      <div className="space-y-2">
        {shown.map((d) => (
          <div key={d.id} className="ui-card">
            <div className="flex flex-wrap items-center gap-2">
              <span className={`inline-block h-3 w-3 rounded-sm border ${d.veg ? 'border-green-700 bg-green-600' : 'border-red-900 bg-red-700'}`} />
              <span className="font-medium text-white">{d.name}</span>
              <span className="text-xs text-neutral-500">used {d.timesUsed}×</span>
              {d.descStatus === 'pending' && <span className="rounded bg-amber-600/20 text-amber-300 px-1.5 py-0.5 text-[10px]">pending</span>}
              <select value={d.category} onChange={(e) => update(d.id, { category: e.target.value })} className="ui-input ml-auto" style={{ width: 'auto' }}>
                <option value="">Uncategorized</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <Badge src={d.catSource} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-neutral-500">À-la-carte price</span>
              <select value={d.priceTier || ''} onChange={(e) => update(d.id, { priceTier: e.target.value })} className="ui-input" style={{ width: 'auto' }}>
                {TIERS.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <span className="text-neutral-500">or override $</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={d.priceCents ? (d.priceCents / 100).toFixed(2) : ''}
                onChange={(e) => update(d.id, { priceCents: e.target.value ? Math.round(parseFloat(e.target.value) * 100) : 0 })}
                placeholder="auto"
                className="ui-input"
                style={{ width: '90px' }}
              />
              <span className="text-neutral-500 ml-3">Pan cost $</span>
              <input
                type="number"
                step="0.01"
                min="0"
                title="Food cost of one full pan/batch — for the buffet food-cost report"
                value={d.panCostCents ? (d.panCostCents / 100).toFixed(2) : ''}
                onChange={(e) => update(d.id, { panCostCents: e.target.value ? Math.round(parseFloat(e.target.value) * 100) : 0 })}
                placeholder="0"
                className="ui-input"
                style={{ width: '90px' }}
              />
            </div>
            <div className="mt-2 flex items-start gap-2">
              <textarea
                value={d.description}
                onChange={(e) => update(d.id, { description: e.target.value })}
                rows={2}
                placeholder={d.descStatus === 'pending' ? 'Awaiting AI description…' : 'Description'}
                className="ui-input flex-1"
              />
              <div className="flex flex-col items-end gap-1">
                <Badge src={d.descSource} />
                <button onClick={() => save(d)} disabled={savingId === d.id} className="ui-btn-primary">
                  {savingId === d.id ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="text-sm text-neutral-500 py-6 text-center">No dishes match.</p>}
      </div>
    </div>
  );
}
